import React, { useCallback, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import RadarIcon from "@mui/icons-material/Radar";
import SearchIcon from "@mui/icons-material/Search";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import api from "../../../services/api/axios";
import BackButton from "../../../shared/components/BackButton";

const BRAND_BLUE = "#00336C";
const BRAND_GREEN = "#648315";
const PAGE_BG = "#f7f9fc";
const GRADE_META = {
  A: { label: "High confidence", color: "#1f7a4d", bg: "rgba(31,122,77,0.1)" },
  B: { label: "Medium confidence", color: "#7a5b12", bg: "rgba(122,91,18,0.12)" },
  C: { label: "Low confidence", color: "#7a2d2d", bg: "rgba(122,45,45,0.1)" },
};
const TIMING_LABELS = {
  future: "Future event",
  likely_future: "Likely future event",
  past: "Past evidence",
};

function formatDate(value) {
  if (!value) return "Date TBD";
  const date = new Date(`${value}T12:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function locationText(show) {
  return [show.city, show.state, show.venue].filter(Boolean).join(" • ") || "Location TBD";
}

function ShowCard({ show }) {
  const grade = GRADE_META[show.confidence_grade] || GRADE_META.C;

  return (
    <Paper
      elevation={0}
      sx={{
        border: "1px solid rgba(0,51,108,0.12)",
        borderRadius: "12px",
        p: 2,
        background: "#ffffff",
        boxShadow: "0 2px 10px rgba(15,23,42,0.04)",
      }}
    >
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "flex-start" }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75, flexWrap: "wrap" }}>
            <Typography sx={{ color: BRAND_BLUE, fontWeight: 800, fontSize: 17 }}>
              {show.show_name}
            </Typography>
            <Chip
              size="small"
              label={show.confidence_grade || "C"}
              sx={{
                height: 24,
                fontWeight: 800,
                color: grade.color,
                backgroundColor: grade.bg,
              }}
            />
          </Stack>
          <Typography sx={{ color: "#334155", fontSize: 14, mb: 0.5 }}>
            {formatDate(show.start_date)}
            {show.end_date && show.end_date !== show.start_date ? ` - ${formatDate(show.end_date)}` : ""}
          </Typography>
          <Typography sx={{ color: "#64748b", fontSize: 13.5 }}>{locationText(show)}</Typography>
          {(show.naics_classifications || []).map((classification, index) => (
            <Typography key={`${classification.naics_code}-${index}`} sx={{ color: "#64748b", fontSize: 13, mt: 0.5 }}>
              NAICS {classification.naics_code}{classification.naics_title ? ` — ${classification.naics_title}` : ""}
            </Typography>
          ))}
          {show.booth && (
            <Typography sx={{ color: "#475569", fontSize: 13.5, mt: 1 }}>
              Booth {show.booth}
            </Typography>
          )}
          {show.source_snippet && (
            <Typography sx={{ color: "#64748b", fontSize: 13.5, mt: 1.25, lineHeight: 1.55 }}>
              {show.source_snippet}
            </Typography>
          )}
        </Box>
        <Stack alignItems={{ xs: "flex-start", sm: "flex-end" }} spacing={1}>
          <Chip
            size="small"
            label={grade.label}
            sx={{
              color: grade.color,
              backgroundColor: grade.bg,
              fontWeight: 700,
            }}
          />
          {show.source_url && (
            <Button
              component="a"
              href={show.source_url}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              endIcon={<OpenInNewIcon sx={{ fontSize: "15px !important" }} />}
              sx={{
                color: BRAND_BLUE,
                fontWeight: 700,
                textTransform: "none",
                px: 0,
                "&:hover": { backgroundColor: "transparent", color: BRAND_GREEN },
              }}
            >
              Source
            </Button>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
}

function AiSuggestionCard({ suggestion }) {
  const grade = GRADE_META[suggestion.confidence_grade] || GRADE_META.C;

  return (
    <Paper
      elevation={0}
      sx={{
        border: "1px solid rgba(100,131,21,0.24)",
        borderRadius: "12px",
        p: 2,
        background: "rgba(100,131,21,0.035)",
      }}
    >
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "flex-start" }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75, flexWrap: "wrap" }}>
            <Typography sx={{ color: BRAND_BLUE, fontWeight: 800, fontSize: 17 }}>
              {suggestion.show_name}
            </Typography>
            <Chip
              size="small"
              label={suggestion.confidence_grade || "C"}
              sx={{ height: 24, fontWeight: 800, color: grade.color, backgroundColor: grade.bg }}
            />
            {suggestion.timing && (
              <Chip
                size="small"
                label={TIMING_LABELS[suggestion.timing] || suggestion.timing}
                sx={{
                  height: 24,
                  fontWeight: 700,
                  color: BRAND_BLUE,
                  backgroundColor: "rgba(0,51,108,0.07)",
                }}
              />
            )}
          </Stack>
          <Typography sx={{ color: "#334155", fontSize: 14, mb: 0.5 }}>
            {[suggestion.year, suggestion.date].filter(Boolean).join(" • ") || "Date TBD"}
          </Typography>
          <Typography sx={{ color: "#64748b", fontSize: 13.5 }}>
            {suggestion.location || "Location TBD"}
          </Typography>
          {suggestion.reason && (
            <Typography sx={{ color: "#475569", fontSize: 13.5, mt: 1.25, lineHeight: 1.55 }}>
              {suggestion.reason}
            </Typography>
          )}
          {suggestion.timing === "likely_future" && (
            <Typography sx={{ color: "#7a5b12", fontSize: 13, mt: 0.75, fontWeight: 700 }}>
              Predicted appearance; company participation is unconfirmed.
            </Typography>
          )}
          {suggestion.source_snippet && (
            <Typography sx={{ color: "#64748b", fontSize: 13.5, mt: 1, lineHeight: 1.55 }}>
              {suggestion.source_snippet}
            </Typography>
          )}
        </Box>
        <Stack alignItems={{ xs: "flex-start", sm: "flex-end" }} spacing={1}>
          <Chip
            size="small"
            label={`AI • ${grade.label}`}
            sx={{ color: grade.color, backgroundColor: grade.bg, fontWeight: 700 }}
          />
          {suggestion.source_url && (
            <Button
              component="a"
              href={suggestion.source_url}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              endIcon={<OpenInNewIcon sx={{ fontSize: "15px !important" }} />}
              sx={{
                color: BRAND_BLUE,
                fontWeight: 700,
                textTransform: "none",
                px: 0,
                "&:hover": { backgroundColor: "transparent", color: BRAND_GREEN },
              }}
            >
              Source
            </Button>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
}

export default function TradeShowRadarPage() {
  const [query, setQuery] = useState("");
  const [matches, setMatches] = useState([]);
  const [activeCompany, setActiveCompany] = useState(null);
  const [results, setResults] = useState(null);
  const [aiResults, setAiResults] = useState([]);
  const [aiSearchedQuery, setAiSearchedQuery] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingShows, setLoadingShows] = useState(false);
  const [error, setError] = useState("");
  const [aiError, setAiError] = useState("");

  const upcoming = useMemo(() => (Array.isArray(results?.upcoming) ? results.upcoming : []), [results]);
  const later = useMemo(() => (Array.isArray(results?.later) ? results.later : []), [results]);
  const undated = useMemo(() => (Array.isArray(results?.undated) ? results.undated : []), [results]);
  const past = useMemo(() => (Array.isArray(results?.past) ? results.past : []), [results]);
  const aiFuture = useMemo(() => aiResults.filter((item) => item.timing !== "past"), [aiResults]);
  const aiPast = useMemo(() => aiResults.filter((item) => item.timing === "past"), [aiResults]);

  const loadShows = useCallback(async (company) => {
    if (!company?.company_id) return;
    setActiveCompany(company);
    setLoadingShows(true);
    setError("");

    try {
      const { data } = await api.get(`/trade-show-radar/companies/${company.company_id}/shows`, {
        params: { window: "12m", include_past: true },
      });
      setResults(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to load trade show matches.");
      setResults(null);
    } finally {
      setLoadingShows(false);
    }
  }, []);

  const runAiSearch = useCallback(async (searchQuery) => {
    const trimmed = searchQuery.trim();
    if (!trimmed) return;

    setAiLoading(true);
    setAiError("");
    setAiResults([]);
    setAiSearchedQuery(trimmed);

    try {
      const { data } = await api.post("/trade-show-radar/ai-search", { query: trimmed }, { timeout: 120000 });
      setAiResults(Array.isArray(data?.results) ? data.results : []);
    } catch (err) {
      setAiError(err?.response?.data?.detail || "Unable to search with AI right now.");
    } finally {
      setAiLoading(false);
    }
  }, []);

  const handleSearch = useCallback(async () => {
    const trimmed = query.trim();
    if (!trimmed) return;

    setLoading(true);
    setError("");
    setAiError("");
    setAiResults([]);
    setAiSearchedQuery("");
    setResults(null);
    setActiveCompany(null);

    try {
      const { data } = await api.get("/trade-show-radar/companies/search", {
        params: { q: trimmed, limit: 10 },
      });
      const rows = Array.isArray(data) ? data : [];
      setMatches(rows);
      if (rows.length > 0) {
        await loadShows(rows[0]);
      } else {
        await runAiSearch(trimmed);
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to search Trade Show Radar.");
      setMatches([]);
    } finally {
      setLoading(false);
    }
  }, [loadShows, query, runAiSearch]);

  return (
    <Box sx={{ width: "100%", minHeight: "100vh", backgroundColor: PAGE_BG, pb: 3 }}>
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 5,
          borderBottom: "1px solid #dce4ef",
          background: "#ffffff",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: { xs: 2, md: 3 },
            py: 1.75,
            position: "relative",
          }}
        >
          <BackButton to="/tradeshows" label="Back" />
          <Box
            sx={{
              position: { xs: "static", md: "absolute" },
              left: { md: "50%" },
              transform: { md: "translateX(-50%)" },
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 2,
                background: BRAND_BLUE,
                color: "#ffffff",
                display: "grid",
                placeItems: "center",
                flex: "0 0 auto",
              }}
            >
              <RadarIcon sx={{ fontSize: 19 }} />
            </Box>
            <Typography
              sx={{
                fontSize: "1.05rem",
                fontWeight: 800,
                color: BRAND_GREEN,
                letterSpacing: "-0.01em",
                whiteSpace: "nowrap",
              }}
            >
              Trade Show Radar
              </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ px: { xs: 2, md: 3 }, pt: 3 }}>
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: "none",
            mx: "auto",
            border: "1px solid rgba(0,51,108,0.12)",
            borderRadius: "14px",
            background: "#ffffff",
            boxShadow: "0 8px 22px rgba(15,23,42,0.06)",
            overflow: "hidden",
          }}
        >
          <Box sx={{ p: { xs: 2, md: 3 }, borderTop: `4px solid ${BRAND_BLUE}` }}>
            <Typography sx={{ color: BRAND_BLUE, fontSize: 24, fontWeight: 850, mb: 0.75 }}>
              Find likely trade show appearances by company
            </Typography>
            <Typography sx={{ color: "#5c718f", fontSize: 14.5, lineHeight: 1.6, maxWidth: 760, mb: 2.5 }}>
              Search a company name or website to see matched trade show editions, confidence grades, and source
              evidence from the integrated Trade Show Radar dataset.
            </Typography>

            <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
              <TextField
                fullWidth
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    handleSearch();
                  }
                }}
                placeholder="Search by company name or domain"
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "#7890ad" }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                onClick={handleSearch}
                disabled={loading || aiLoading || !query.trim()}
                sx={{
                  minWidth: 132,
                  height: 40,
                  borderRadius: "10px",
                  backgroundColor: BRAND_GREEN,
                  color: "#ffffff",
                  fontWeight: 800,
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#526d11" },
                  "&.Mui-disabled": { backgroundColor: "#d5dec2", color: "#ffffff" },
                }}
              >
                {loading || aiLoading ? <CircularProgress size={18} sx={{ color: "#ffffff" }} /> : "Search"}
              </Button>
            </Stack>

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            {aiError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {aiError}
              </Alert>
            )}

            {matches.length > 0 && (
              <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: "wrap", rowGap: 1 }}>
                {matches.map((company) => {
                  const selected = activeCompany?.company_id === company.company_id;
                  return (
                    <Chip
                      key={company.company_id}
                      label={company.domain ? `${company.canonical_name} (${company.domain})` : company.canonical_name}
                      onClick={() => loadShows(company)}
                      sx={{
                        borderRadius: "999px",
                        color: selected ? "#ffffff" : BRAND_BLUE,
                        backgroundColor: selected ? BRAND_BLUE : "rgba(0,51,108,0.06)",
                        border: `1px solid ${selected ? BRAND_BLUE : "rgba(0,51,108,0.14)"}`,
                        fontWeight: 700,
                        "&:hover": {
                          backgroundColor: selected ? BRAND_BLUE : "rgba(0,51,108,0.1)",
                        },
                      }}
                    />
                  );
                })}
              </Stack>
            )}
          </Box>

          <Divider />

          <Box sx={{ p: { xs: 2, md: 3 }, minHeight: 300 }}>
            {!activeCompany && !loading && matches.length === 0 && !query.trim() && (
              <Box sx={{ textAlign: "center", py: 7 }}>
                <RadarIcon sx={{ fontSize: 46, color: "rgba(0,51,108,0.22)", mb: 1 }} />
                <Typography sx={{ color: BRAND_BLUE, fontWeight: 800, fontSize: 18 }}>
                  Search to start
                </Typography>
                <Typography sx={{ color: "#64748b", fontSize: 14, mt: 0.5 }}>
                  Results will appear here when a matching company exists in Trade Show Radar.
                </Typography>
              </Box>
            )}

            {!loading && matches.length === 0 && query.trim() && !error && (
              <Box sx={{ textAlign: "center", py: 7 }}>
                <Typography sx={{ color: BRAND_BLUE, fontWeight: 800, fontSize: 18 }}>
                  No database match found
                </Typography>
                <Typography sx={{ color: "#64748b", fontSize: 14, mt: 0.5 }}>
                  Trade Show Radar searched the database first, then checked the web with AI.
                </Typography>
              </Box>
            )}

            {(aiLoading || aiSearchedQuery) && (
              <Box sx={{ mt: activeCompany ? 3 : 0 }}>
                <Typography sx={{ color: BRAND_BLUE, fontWeight: 850, fontSize: 16, mb: 0.5 }}>
                  AI trade show research
                </Typography>
                <Typography sx={{ color: "#64748b", fontSize: 13.5, mb: 1.5 }}>
                  Future appearances for {aiSearchedQuery || query} include confirmed events and predictions based on
                  past evidence. Review the source and confidence on each result.
                </Typography>
                {aiLoading ? (
                  <Box sx={{ display: "grid", placeItems: "center", py: 4 }}>
                    <CircularProgress size={24} sx={{ color: BRAND_GREEN }} />
                  </Box>
                ) : aiResults.length > 0 ? (
                  <Stack spacing={3}>
                    <Box>
                      <Typography sx={{ color: BRAND_BLUE, fontWeight: 800, fontSize: 15, mb: 1 }}>
                        Future appearances and predictions
                      </Typography>
                      {aiFuture.length > 0 ? (
                        <Stack spacing={1.5}>
                          {aiFuture.map((suggestion, index) => (
                            <AiSuggestionCard key={`${suggestion.show_name}-${suggestion.year}-${index}`} suggestion={suggestion} />
                          ))}
                        </Stack>
                      ) : (
                        <Typography sx={{ color: "#64748b", fontSize: 14 }}>
                          No credible future appearances or predictions found yet.
                        </Typography>
                      )}
                    </Box>
                    {aiPast.length > 0 && (
                      <Box>
                        <Typography sx={{ color: BRAND_BLUE, fontWeight: 800, fontSize: 15, mb: 1 }}>
                          Past evidence
                        </Typography>
                        <Stack spacing={1.5}>
                          {aiPast.map((suggestion, index) => (
                            <AiSuggestionCard key={`${suggestion.show_name}-${suggestion.year}-${index}`} suggestion={suggestion} />
                          ))}
                        </Stack>
                      </Box>
                    )}
                  </Stack>
                ) : (
                  <Typography sx={{ color: "#64748b", fontSize: 14 }}>
                    No credible AI trade show suggestions were found.
                  </Typography>
                )}
              </Box>
            )}

            {activeCompany && (
              <>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  justifyContent="space-between"
                  spacing={1.5}
                  sx={{ mb: 2.25 }}
                >
                  <Box>
                    <Typography sx={{ color: BRAND_BLUE, fontWeight: 850, fontSize: 20 }}>
                      {activeCompany.canonical_name}
                    </Typography>
                    <Typography sx={{ color: "#64748b", fontSize: 13.5 }}>
                      {activeCompany.domain || "No domain on file"}
                    </Typography>
                    {activeCompany.naics_code && (
                      <Typography sx={{ color: "#64748b", fontSize: 13.5, mt: 0.5 }}>
                        NAICS {activeCompany.naics_code}{activeCompany.naics_title ? ` — ${activeCompany.naics_title}` : ""}
                        {activeCompany.sector ? ` · ${activeCompany.sector}` : ""}
                      </Typography>
                    )}
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap", rowGap: 1 }}>
                    <Chip label={`${upcoming.length} upcoming`} sx={{ color: BRAND_BLUE, fontWeight: 800 }} />
                    {later.length > 0 && <Chip label={`${later.length} later`} sx={{ color: BRAND_BLUE, fontWeight: 700 }} />}
                    {undated.length > 0 && <Chip label={`${undated.length} date unconfirmed`} sx={{ color: "#475569", fontWeight: 700 }} />}
                    <Chip label={`${past.length} past`} sx={{ color: "#475569", fontWeight: 700 }} />
                  </Stack>
                </Stack>

                {loadingShows ? (
                  <Box sx={{ display: "grid", placeItems: "center", py: 7 }}>
                    <CircularProgress size={26} sx={{ color: BRAND_GREEN }} />
                  </Box>
                ) : (
                  <Stack spacing={3}>
                    <Box>
                      <Typography sx={{ color: BRAND_BLUE, fontWeight: 850, fontSize: 16, mb: 1.25 }}>
                        Upcoming matches
                      </Typography>
                      {upcoming.length > 0 ? (
                        <Stack spacing={1.5}>
                          {upcoming.map((show) => (
                            <ShowCard key={show.participation_id} show={show} />
                          ))}
                        </Stack>
                      ) : (
                        <Typography sx={{ color: "#64748b", fontSize: 14 }}>
                          No upcoming trade show appearances found in the next 12 months.
                        </Typography>
                      )}
                    </Box>

                    {later.length > 0 && (
                      <Box>
                        <Typography sx={{ color: BRAND_BLUE, fontWeight: 850, fontSize: 16, mb: 1.25 }}>
                          Beyond the next 12 months
                        </Typography>
                        <Stack spacing={1.5}>
                          {later.map((show) => <ShowCard key={show.participation_id} show={show} />)}
                        </Stack>
                      </Box>
                    )}

                    {undated.length > 0 && (
                      <Box>
                        <Typography sx={{ color: BRAND_BLUE, fontWeight: 850, fontSize: 16, mb: 1.25 }}>
                          Date unconfirmed
                        </Typography>
                        <Stack spacing={1.5}>
                          {undated.map((show) => <ShowCard key={show.participation_id} show={show} />)}
                        </Stack>
                      </Box>
                    )}

                    <Box>
                      <Typography sx={{ color: BRAND_BLUE, fontWeight: 850, fontSize: 16, mb: 1.25 }}>
                        Past evidence
                      </Typography>
                      {past.length > 0 ? (
                        <Stack spacing={1.5}>
                          {past.slice(0, 8).map((show) => (
                            <ShowCard key={show.participation_id} show={show} />
                          ))}
                        </Stack>
                      ) : (
                        <Typography sx={{ color: "#64748b", fontSize: 14 }}>
                          No past trade show evidence found for this company.
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                )}
              </>
            )}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}
